import jexlInstance from "jexl";

const jexl = new jexlInstance.Jexl();

// ── Array transforms ─────────────────────────────────────────────────
jexl.addTransform("join", (val, sep) =>
  Array.isArray(val) ? val.join(sep ?? ", ") : val
);

jexl.addTransform("map", (val, key) =>
  Array.isArray(val) ? val.map((item) => item?.[key]) : val
);

jexl.addTransform("filter", (val, key) =>
  Array.isArray(val) ? val.filter((item) => !!item?.[key]) : val
);

jexl.addTransform("first", (val) =>
  Array.isArray(val) ? val[0] : val
);

jexl.addTransform("last", (val) =>
  Array.isArray(val) ? val[val.length - 1] : val
);

jexl.addTransform("at", (val, index) =>
  Array.isArray(val) ? val[index] : val
);

jexl.addTransform("count", (val) =>
  Array.isArray(val) ? val.length : val != null ? 1 : 0
);

jexl.addTransform("flat", (val) =>
  Array.isArray(val) ? val.flat() : val
);

jexl.addTransform("unique", (val) =>
  Array.isArray(val) ? [...new Set(val)] : val
);

jexl.addTransform("reverse", (val) =>
  Array.isArray(val) ? [...val].reverse() : val
);

jexl.addTransform("sort", (val, key) => {
  if (!Array.isArray(val)) return val;
  return [...val].sort((a, b) => {
    const av = key ? a?.[key] : a;
    const bv = key ? b?.[key] : b;
    return av < bv ? -1 : av > bv ? 1 : 0;
  });
});

jexl.addTransform("slice", (val, start, end) =>
  Array.isArray(val) ? val.slice(start, end) : val
);

jexl.addTransform("pluck", (val, ...keys) => {
  if (!Array.isArray(val)) return val;
  if (keys.length === 1) return val.map((item) => item?.[keys[0]]);
  return val.map((item) => {
    const out = {};
    keys.forEach((k) => { out[k] = item?.[k]; });
    return out;
  });
});

// ── String transforms ────────────────────────────────────────────────
jexl.addTransform("upper", (val) =>
  typeof val === "string" ? val.toUpperCase() : val
);

jexl.addTransform("lower", (val) =>
  typeof val === "string" ? val.toLowerCase() : val
);

jexl.addTransform("trim", (val) =>
  typeof val === "string" ? val.trim() : val
);

jexl.addTransform("replace", (val, search, replacement) =>
  typeof val === "string" ? val.replaceAll(search, replacement ?? "") : val
);

jexl.addTransform("split", (val, sep) =>
  typeof val === "string" ? val.split(sep ?? ",") : val
);

jexl.addTransform("substr", (val, start, len) =>
  typeof val === "string" ? val.substring(start, len != null ? start + len : undefined) : val
);

jexl.addTransform("capitalize", (val) =>
  typeof val === "string" && val.length > 0
    ? val[0].toUpperCase() + val.slice(1)
    : val
);

jexl.addTransform("padStart", (val, len, fill) =>
  typeof val === "string" ? val.padStart(len, fill ?? " ") : val
);

jexl.addTransform("padEnd", (val, len, fill) =>
  typeof val === "string" ? val.padEnd(len, fill ?? " ") : val
);

// ── Number / formatting transforms ──────────────────────────────────
jexl.addTransform("toFixed", (val, digits) =>
  typeof val === "number" ? val.toFixed(digits ?? 2) : val
);

jexl.addTransform("round", (val) =>
  typeof val === "number" ? Math.round(val) : val
);

jexl.addTransform("floor", (val) =>
  typeof val === "number" ? Math.floor(val) : val
);

jexl.addTransform("ceil", (val) =>
  typeof val === "number" ? Math.ceil(val) : val
);

jexl.addTransform("abs", (val) =>
  typeof val === "number" ? Math.abs(val) : val
);

jexl.addTransform("currency", (val, symbol, decimals) => {
  const num = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(num)) return val;
  const s = symbol ?? "";
  const d = decimals ?? 2;
  return s + num.toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
});

jexl.addTransform("percent", (val, decimals) => {
  const num = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(num)) return val;
  return (num * 100).toFixed(decimals ?? 0) + "%";
});

// ── Date transforms ─────────────────────────────────────────────────
jexl.addTransform("formatDate", (val, fmt) => {
  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return val;
  const pad = (n) => String(n).padStart(2, "0");
  const tokens = {
    YYYY: d.getFullYear(),
    MM: pad(d.getMonth() + 1),
    DD: pad(d.getDate()),
    HH: pad(d.getHours()),
    mm: pad(d.getMinutes()),
    ss: pad(d.getSeconds()),
  };
  let result = fmt || "DD.MM.YYYY";
  Object.keys(tokens).forEach((t) => {
    result = result.replace(t, tokens[t]);
  });
  return result;
});

// ── Object transforms ───────────────────────────────────────────────
jexl.addTransform("keys", (val) =>
  val && typeof val === "object" && !Array.isArray(val) ? Object.keys(val) : val
);

jexl.addTransform("values", (val) =>
  val && typeof val === "object" && !Array.isArray(val) ? Object.values(val) : val
);

jexl.addTransform("entries", (val) =>
  val && typeof val === "object" && !Array.isArray(val)
    ? Object.entries(val).map(([k, v]) => ({ key: k, value: v }))
    : val
);

jexl.addTransform("get", (val, path) => {
  if (val == null || typeof path !== "string") return val;
  return path.split(".").reduce((o, k) => o?.[k], val);
});

// ── General transforms ──────────────────────────────────────────────
jexl.addTransform("default", (val, fallback) =>
  val == null || val === "" ? fallback : val
);

jexl.addTransform("toString", (val) =>
  val != null ? String(val) : ""
);

jexl.addTransform("toJSON", (val) => {
  try { return JSON.stringify(val); } catch { return val; }
});

jexl.addTransform("type", (val) => {
  if (val === null) return "null";
  if (Array.isArray(val)) return "array";
  return typeof val;
});

export async function evaluateExpression(expression, context) {
  try {
    return await jexl.eval(expression, context || {});
  } catch (err) {
    console.warn("[jexl] evaluation error:", err.message, { expression });
    return null;
  }
}

export function evaluateExpressionSync(expression, context) {
  try {
    return jexl.evalSync(expression, context || {});
  } catch (err) {
    console.warn("[jexl] evaluation error:", err.message, { expression });
    return null;
  }
}

export function listTransforms() {
  return [
    { name: "join", args: "separator?", example: 'items|join(", ")', description: "Join array elements into a string" },
    { name: "map", args: "key", example: 'items|map("name")', description: "Extract a property from each array element" },
    { name: "filter", args: "key", example: 'items|filter("active")', description: "Keep elements where key is truthy" },
    { name: "first", args: "", example: "items|first", description: "Get the first element of an array" },
    { name: "last", args: "", example: "items|last", description: "Get the last element of an array" },
    { name: "at", args: "index", example: "items|at(2)", description: "Get element at a specific index" },
    { name: "count", args: "", example: "items|count", description: "Count array elements" },
    { name: "flat", args: "", example: "items|flat", description: "Flatten nested arrays one level" },
    { name: "unique", args: "", example: "items|unique", description: "Remove duplicate values" },
    { name: "reverse", args: "", example: "items|reverse", description: "Reverse array order" },
    { name: "sort", args: "key?", example: 'items|sort("name")', description: "Sort array, optionally by key" },
    { name: "slice", args: "start, end?", example: "items|slice(0, 5)", description: "Get a sub-array" },
    { name: "pluck", args: "key1, key2, ...", example: 'items|pluck("name", "email")', description: "Pick specific keys from each element" },
    { name: "upper", args: "", example: "name|upper", description: "Convert to uppercase" },
    { name: "lower", args: "", example: "name|lower", description: "Convert to lowercase" },
    { name: "trim", args: "", example: "name|trim", description: "Remove leading/trailing whitespace" },
    { name: "replace", args: "search, replacement?", example: 'text|replace("-", " ")', description: "Replace all occurrences" },
    { name: "split", args: "separator?", example: 'tags|split(",")', description: "Split string into array" },
    { name: "capitalize", args: "", example: "name|capitalize", description: "Capitalize first letter" },
    { name: "toFixed", args: "digits?", example: "price|toFixed(2)", description: "Format number to fixed decimals" },
    { name: "round", args: "", example: "value|round", description: "Round to nearest integer" },
    { name: "currency", args: "symbol?, decimals?", example: 'price|currency("$", 2)', description: "Format as currency" },
    { name: "percent", args: "decimals?", example: "rate|percent(1)", description: "Format as percentage (0.15 → 15%)" },
    { name: "formatDate", args: "format?", example: 'date|formatDate("DD.MM.YYYY")', description: "Format a date string" },
    { name: "keys", args: "", example: "obj|keys", description: "Get object keys as array" },
    { name: "values", args: "", example: "obj|values", description: "Get object values as array" },
    { name: "entries", args: "", example: "obj|entries", description: "Convert object to [{key, value}] array" },
    { name: "get", args: "path", example: 'data|get("address.city")', description: "Access nested property by dot-path" },
    { name: "default", args: "fallback", example: 'name|default("N/A")', description: "Use fallback when value is null/empty" },
    { name: "toString", args: "", example: "value|toString", description: "Convert to string" },
    { name: "toJSON", args: "", example: "value|toJSON", description: "Serialize to JSON string" },
    { name: "type", args: "", example: "value|type", description: "Get value type (string, number, array, object, null)" },
  ];
}

export default jexl;
